import React from "react";
import { Hash, Loader2, Check } from "lucide-react";

import Input from "src/components/ui/Input";
import { IdentifierAvailabilityStatus } from "src/hooks/useIdentifierAvailability";
import { IDENTIFIER_MAX_LENGTH } from "src/utils/authFormValidation";

interface IdentifierFieldProps {
  value: string;
  onChange: (value: string) => void;
  status: IdentifierAvailabilityStatus;
  suggestions: string[];
  error?: string | null;
  label?: string;
  placeholder?: string;
}

// Presentational — status/suggestions/error come from
// useIdentifierAvailability called in the parent, which also needs
// `status` to gate its submit button. Shared by the sign-up and admin
// tenant-creation wizards.
const IdentifierField: React.FC<IdentifierFieldProps> = ({
  value,
  onChange,
  status,
  suggestions,
  error,
  label = "Organization identifier",
  placeholder = "your-company-identifier",
}) => {
  // `error` overrides the hook's own message — used for a "required" error
  // set by the parent on submit attempt, which the hook itself has no
  // concept of (an empty field is just "idle" to it).
  const helperText =
    error ||
    (status === "taken"
      ? "That identifier is already taken — try one of the suggestions below."
      : status === "available"
      ? "Available."
      : "A short internal identifier for your organization. You can change this later.");

  return (
    <div>
      <Input
        label={label}
        required
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={IDENTIFIER_MAX_LENGTH}
        startAdornment={<Hash size={16} />}
        endAdornment={
          status === "checking" ? (
            <Loader2 size={15} className="animate-spin text-gray-400" />
          ) : status === "available" ? (
            <Check size={16} className="text-green-500" />
          ) : undefined
        }
        error={!!error || status === "taken" || status === "invalid"}
        helperText={helperText || undefined}
      />
      {status === "taken" && suggestions.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onChange(s)}
              className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300 dark:hover:border-primary-700 dark:hover:bg-primary-900/30 dark:hover:text-primary-300"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default IdentifierField;
